import React, { Fragment } from "react"
import classNames from "classnames"
import { Field, FieldArray, ErrorMessage } from "formik"
import styles from "./utils/form.module.scss"
import { generateFormField, getFollowUpQuestionData } from "./services/form"
import { sanitizeHtml } from "../../../services/sanitizeHtml"
// import { camelize } from "humps"
/**
 ** Checkbox group linked in formik.
 ** Supported parameters:
 **
 **  {
 **               'name': String,
 **               'values': Object [],
 **               'label': String,
 **               'options': Object [],
 **               'onChange': Function,
 **               ...props compatible in Field Component of Formik
 **             }
 **/
const FormCheckbox = ({
  name,
  options,
  values,
  value = [],
  onChange,
  title,
  labelIconColor,
  labelIcon,
  hideOptional,
  helper,
  helperClassName,
  isRequired,
  restrictedOption,
  className,
  followUpQuestions = [],
  formFields,
  formValues,
  setFieldValue,
}) => {
  let fieldValues = values || value
  // Custom handleChange of every checkbox in the group
  //
  // Note: Curried function: need the form from the Field and option (text from values)
  //       to return then (event) => { ... }
  const handleChange = (name, form, option, isChecked) => (event) => {
    const { setFieldValue } = form
    let newValue = [...fieldValues]
    if (event.target.checked) newValue.push(option)
    else newValue = newValue.filter((element) => element !== option)
    setFieldValue(name, newValue)

    if (onChange && name === "consentAndAuthorization")
      onChange(event, newValue)
    else if (onChange && name !== "consentAndAuthorization")
      onChange({ event, newValue, option: option, setFieldValue, isChecked })
  }

  let disabledOptions = []

  if (!!restrictedOption) {
    let restrictedOptions = restrictedOption.split(" | ") || []
    let currentSelectedRestrictedOption = restrictedOptions.find(
      (restrictedOption) => fieldValues.includes(restrictedOption)
    )
    if (!!currentSelectedRestrictedOption) {
      fieldValues = [currentSelectedRestrictedOption]
      disabledOptions = options.filter(
        (option) => option !== currentSelectedRestrictedOption
      )
    } else if (!currentSelectedRestrictedOption && fieldValues.length > 0) {
      disabledOptions = [...restrictedOptions]
    }
  }

  const CheckBox = ({ form, option, index }) => (
    <Fragment>
      <input
        className="is-checkradio"
        type="checkbox"
        checked={fieldValues.find((element) => element === option)}
        onChange={handleChange(
          name,
          form,
          option,
          !fieldValues.find((element) => element === option)
        )}
        id={`${name}[${index}]`}
        name={`${name}[${index}]`}
        disabled={disabledOptions?.find((element) => element === option)}
      />
      <label
        className={classNames("checkbox-label", styles["formCheckbox__label"])}
        for={`${name}[${index}]`}
        htmlFor={`${name}[${index}]`}
      >
        <span className="ml-1">
          <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(option) }} />
          {/* {option} */}
        </span>
      </label>
    </Fragment>
  )

  return (
    <div className={classNames(styles["formCheckbox"])}>
      {title && (
        <label className={classNames("label has-text-weight-normal")}>
          {!!labelIcon && (
            <span className={`icon has-text-${labelIconColor}`}>
              {labelIcon}
            </span>
          )}
          <span> {title}</span>
          {!isRequired && !hideOptional && (
            <span className="has-text-grey is-italic"> (Optional)</span>
          )}
          {!!helper && (
            <span
              className={classNames(
                "helper has-text-weight-normal",
                helperClassName
              )}
            >
              <br />
              {helper}
            </span>
          )}
        </label>
      )}
      <FieldArray name={name}>
        {() =>
          options.map((option, index) => (
            <div key={index} className="mb-1">
              <Field>
                {({ form }) => (
                  <div className="field">
                    <CheckBox form={form} option={option} index={index} />
                    {followUpQuestions?.length > 0 &&
                      followUpQuestions.map((followUpQuestion) => {
                        const getFormField = getFollowUpQuestionData({
                          followUpQuestion,
                          formFields: formFields,
                        })

                        if (
                          values.includes(option) &&
                          getFormField?.referenceAnswer === option
                        )
                          return (
                            <div className="notification is-light ml-2 mt-1">
                              {generateFormField({
                                formField: {
                                  ...getFormField,
                                },
                                formFields,
                                values: formValues,
                                setFieldValue,
                                isFollowUpQuestion: true,
                              })}
                            </div>
                          )
                        return null
                      })}
                  </div>
                )}
              </Field>
            </div>
          ))
        }
      </FieldArray>
      <p className="helper has-text-danger mt-0 mb-1">
        <ErrorMessage name={name} />
      </p>
    </div>
  )
}

export default FormCheckbox
